import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import {
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DiamondIcon from "@mui/icons-material/Diamond";
import { getRoomActivities } from "../../services/budgetService";
import { renderCurrencyIconColored } from "../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../utils/CommonFunction";

const getTypeIcon = (type) => {
  switch (type) {
    case "Design":
      return "🎨";
    case "Installation":
      return "🔧";
    case "Testing":
      return "✅";
    case "Inspection":
      return "🔍";
    case "Delivery":
      return "🚚";
    default:
      return "📦";
  }
};

const formatStatus = (status) => {
  if (!status) return "Pending";
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const formatAmount = (v) => {
  if (v == null || v === "") return "-";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
      }}
    >
      {renderCurrencyIconColored("black-small")}
      {formattedAmountWithoutAed(v)}
    </span>
  );
};

const RoomActivities = ({ roomId, isMobile = false, isTablet = false }) => {
  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scopeActivities, setScopeActivities] = useState([]);
  const [expandedScopes, setExpandedScopes] = useState(new Set());

  const compact = isMobile || isTablet;

  useEffect(() => {
    const load = async () => {
      if (!roomId) return;
      setLoading(true);
      try {
        const res = await getRoomActivities(roomId);
        const list = res?.data || [];
        setScopeActivities(list);
        setExpandedScopes(new Set(list.map((s) => s.scope_id ?? s.id)));
      } catch (e) {
        setError(e?.message || "Failed to load activities");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [roomId]);

  const toggleScope = (scopeId) => {
    setExpandedScopes((prev) => {
      const next = new Set(prev);
      next.has(scopeId) ? next.delete(scopeId) : next.add(scopeId);
      return next;
    });
  };

  const handleActivityClick = (activityId) => {
    const referrer = `/project/details/${projectId}/room/${roomId}`;
    navigate(
      `/admin/projects/${projectId}/milestones/${activityId}?referrer=${encodeURIComponent(referrer)}`
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: "center", color: "#DC2626" }}>
        Error loading activities: {error}
      </Box>
    );
  }

  if (!scopeActivities.length) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 6,
          backgroundColor: "#fff",
          borderRadius: "8px",
          border: "1px solid #E5E7EB",
        }}
      >
        <Typography sx={{ color: "#6B7280" }}>
          No activities found for this room
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, marginTop: "1.2rem" }}>
      {scopeActivities.map((scope) => {
        const scopeId = scope.scope_id ?? scope.id;
        const scopeName = scope.scope_name ?? scope.name ?? "Scope";
        const activities = scope.activities || [];
        const isExpanded = expandedScopes.has(scopeId);

        const startDates = activities.map((a) => a.start_date).filter(Boolean);
        const endDates = activities.map((a) => a.end_date).filter(Boolean);
        const minStart =
          startDates.length > 0
            ? moment.min(startDates.map((d) => moment(d)))
            : null;
        const maxEnd =
          endDates.length > 0
            ? moment.max(endDates.map((d) => moment(d)))
            : null;

        return (
          <Box
            key={scopeId}
            sx={{
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
          >
            <Box
              onClick={() => toggleScope(scopeId)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#F9FAFB",
                p: 2,
                cursor: "pointer",
                flexWrap: compact ? "wrap" : "nowrap",
                gap: 1,
              }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: "18px", fontWeight: 500, color: "#0d0d0e" }}
                >
                  {scopeName}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    fontSize: "14px",
                    color: "#0d0d0e",
                    flexWrap: "wrap",
                  }}
                >
                  <span>
                    Start: {minStart ? minStart.format("DD MMM, YYYY") : "-"}
                  </span>
                  <span>•</span>
                  <span>
                    End: {maxEnd ? maxEnd.format("DD MMM, YYYY") : "-"}
                  </span>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  label={`${activities.length} Activities`}
                  size="small"
                  sx={{
                    backgroundColor: "#EEF2FF",
                    color: "#4338CA",
                    fontWeight: 500,
                  }}
                />
                <ExpandMoreIcon
                  sx={{
                    color: "#6B7280",
                    transition: "transform 200ms",
                    transform: isExpanded ? "rotate(180deg)" : "none",
                  }}
                />
              </Box>
            </Box>

            {isExpanded && (
              <Box sx={{ backgroundColor: "#fff" }}>
                {activities.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4, color: "#6B7280" }}>
                    No activities for this scope
                  </Box>
                ) : (
                  activities.map((activity) =>
                    compact ? (
                      <Box
                        key={activity.id}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.25,
                          mx: 2,
                          my: 1.25,
                          p: 2,
                          borderRadius: "8px",
                          border: "1px solid #E5E7EB",
                          backgroundColor: "#fff",
                        }}
                      >
                        {/* Header: name + amount (top-right) */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 1.5,
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              onClick={() => handleActivityClick(activity.id)}
                              sx={{
                                fontWeight: 500,
                                fontSize: "14px",
                                color: "#1F2937",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                "&:hover": { color: "#2563EB" },
                              }}
                            >
                              {activity.milestone_name}
                              {activity.dependencies?.length > 0 && (
                                <DiamondIcon
                                  sx={{ fontSize: 12, color: "#FB923C" }}
                                />
                              )}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                mt: 0.25,
                                color: "#4B5563",
                              }}
                            >
                              <span>{getTypeIcon(activity.tags)}</span>
                              <Typography
                                sx={{ fontSize: "12px", color: "#949daa" }}
                              >
                                {activity.tags}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                            <Typography
                              sx={{ fontSize: "12px", color: "#949daa" }}
                            >
                              Amount
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#0d0d0e",
                                fontWeight: 500,
                              }}
                            >
                              {formatAmount(activity.total_cost)}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Start & End date in 1 row 1:1 */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{ fontSize: "12px", color: "#949daa" }}
                            >
                              Start Date
                            </Typography>
                            <Typography
                              sx={{ fontSize: "14px", color: "#0d0d0e" }}
                            >
                              {activity?.start_date
                                ? moment(activity.start_date).format(
                                    "DD MMM, YYYY"
                                  )
                                : "-"}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{ fontSize: "12px", color: "#949daa" }}
                            >
                              End Date
                            </Typography>
                            <Typography
                              sx={{ fontSize: "14px", color: "#0d0d0e" }}
                            >
                              {activity?.end_date
                                ? moment(activity.end_date).format(
                                    "DD MMM, YYYY"
                                  )
                                : "-"}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Status: inline label + compact progress, not full row */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#0d0d0e",
                              fontWeight: 500,
                              flexShrink: 0,
                            }}
                          >
                            {formatStatus(activity.status)}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.max(
                              0,
                              Math.min(100, activity.milestone_percentage || 0)
                            )}
                            sx={{
                              flex: 1,
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: "#F3F4F6",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: "#4D6BF6",
                              },
                            }}
                          />
                        </Box>

                        {activity.dependencies?.length ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: 0.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              sx={{ fontSize: "12px", color: "#949daa" }}
                            >
                              Dependencies:
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "13px",
                                color: "#0d0d0e",
                                fontWeight: 500,
                              }}
                            >
                              {activity.dependencies
                                .map((d) => d.name)
                                .join(", ")}
                            </Typography>
                          </Box>
                        ) : null}
                      </Box>
                    ) : (
                    <Box
                      key={activity.id}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        px: 3,
                        py: 2,
                        borderTop: "1px solid #F3F4F6",
                        "&:hover": { backgroundColor: "#F9FAFB" },
                      }}
                    >
                      <Box sx={{ minWidth: "18%" }}>
                        <Typography
                          onClick={() => handleActivityClick(activity.id)}
                          sx={{
                            fontWeight: 500,
                            color: "#1F2937",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            "&:hover": { color: "#2563EB" },
                          }}
                        >
                          {activity.milestone_name}
                          {activity.dependencies?.length > 0 && (
                            <DiamondIcon
                              sx={{ fontSize: 12, color: "#FB923C" }}
                            />
                          )}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "#4B5563",
                        }}
                      >
                        <span>{getTypeIcon(activity.tags)}</span>
                        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                          {activity.tags}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography sx={{ fontSize: "12px", color: "#949daa" }}>
                          Start Date
                        </Typography>
                        <Typography sx={{ fontSize: "14px", color: "#0d0d0e" }}>
                          {activity?.start_date
                            ? moment(activity.start_date).format(
                                "DD MMM, YYYY"
                              )
                            : "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography sx={{ fontSize: "12px", color: "#949daa" }}>
                          End Date
                        </Typography>
                        <Typography sx={{ fontSize: "14px", color: "#0d0d0e" }}>
                          {activity?.end_date
                            ? moment(activity.end_date).format("DD MMM, YYYY")
                            : "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography sx={{ fontSize: "12px", color: "#949daa" }}>
                          Amount
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#0d0d0e",
                            fontWeight: 500,
                          }}
                        >
                          {formatAmount(activity.total_cost)}
                        </Typography>
                      </Box>

                      <Box sx={{ minWidth: 160 }}>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#949daa",
                            mb: 0.5,
                          }}
                        >
                          {formatStatus(activity.status)}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.max(
                            0,
                            Math.min(100, activity.milestone_percentage || 0)
                          )}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "#F3F4F6",
                            "& .MuiLinearProgress-bar": {
                              backgroundColor: "#4D6BF6",
                            },
                          }}
                        />
                      </Box>

                      <Box>
                        <Typography sx={{ fontSize: "12px", color: "#949daa" }}>
                          Dependencies
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#0d0d0e",
                            fontWeight: 500,
                          }}
                        >
                          {activity.dependencies?.length
                            ? activity.dependencies.map((d) => d.name).join(", ")
                            : "None"}
                        </Typography>
                      </Box>
                    </Box>
                    )
                  )
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default RoomActivities;
